"use client";

import * as React from "react";
import ShellFrame, { RAIL_WIDTH } from "@/components/article-shell/ShellFrame";
import { usePersistedWidth } from "@/components/article-shell/shell-primitives";
import { useIsCompactShell } from "@/hooks/useViewport";
import TestCenterShellContext from "./TestCenterShellContext";

/**
 * 시험장 찾기 전용 3컬럼 셸 (JCG-2100 / JCG-2142 ShellFrame 이주).
 *
 * ArticleShell 과 같은 셸 chrome(아이콘 레일 + 슬림 헤더 + sticky 사이드바 + 드래그 리사이즈)을
 * 공유하되, 콘텐츠 구성이 고정이라 ArticleShell 의 지도/목차/점수/풀이 변신 로직을 모두 들어낸
 * 단순 버전이다. chrome 은 ShellFrame 이 단일 소유하고, 이 wrapper 는 우측 폭 영속(ui2-tc-right-w)
 * 과 우측 카드 슬롯(portal 타깃)만 소유한다.
 *
 * - 좌측(col 1): 아이콘 레일(58px)만. 지도를 넓게 쓰기 위해 콘텐츠 컬럼을 두지 않는다
 *   (ShellFrame columns="left+main+right" + showLeftContent=false → 레일만, 좌측 리사이즈 없음).
 * - 가운데(col 2): 슬림 헤더(ContentHeader, 토글 숨김) + 지도(children). 좌측 콘텐츠 컬럼이 없어 폭이 넓다.
 * - 우측(col 3): 카드+RedAlert+배너 슬롯(본문이 createPortal).
 *
 * 데스크탑 전용 — 셸 밖(모바일/클래식)은 Provider 부재(inShell:false)로 단일 컬럼 흐름 렌더(SSR-safe).
 * 스크롤 모델·z-index·다크모드 토큰 규칙은 three-column-layout.md 를 따른다.
 */
const RIGHT_MIN = 280;
const RIGHT_MAX = 520;
const RIGHT_DEFAULT = 340;
const LS_RIGHT = "ui2-tc-right-w";

interface TestCenterShellProps {
  children: React.ReactNode;
  /** 서버 컴포넌트 (Footer, 전역 핸들러) */
  footer: React.ReactNode;
  globals: React.ReactNode;
}

export default function TestCenterShell({
  children,
  footer,
  globals,
}: TestCenterShellProps) {
  // 서버 기본폭 렌더 → 마운트 후 LS 복원(lazy init 금지). setter 는 clamp+영속 일체(JCG-2138).
  const [rightW, onResizeRight] = usePersistedWidth(
    LS_RIGHT,
    RIGHT_DEFAULT,
    RIGHT_MIN,
    RIGHT_MAX,
  );

  // 우측 portal 슬롯 — ref 콜백 setter 로 마운트 시 element, 언마운트 시 null.
  const [rightSlot, setRightSlot] = React.useState<HTMLElement | null>(null);

  const slots = React.useMemo(
    () => ({ inShell: true, rightSlot }),
    [rightSlot],
  );

  // 컴팩트 셸(1024~1279px, JCG-2224): 우측 정보 카드를 기본폭(340)으로 캡해 가운데 지도 폭을
  // 확보한다(좌측은 이미 레일 고정). 영속값(ui2-tc-right-w)은 불변, 렌더 시 유효 폭만 캡.
  const compact = useIsCompactShell();
  const effRightW = compact ? Math.min(rightW, RIGHT_DEFAULT) : rightW;

  return (
    <TestCenterShellContext.Provider value={slots}>
      <ShellFrame
        columns="left+main+right"
        // 좌측은 아이콘 레일만 — 콘텐츠 컬럼 없음(지도를 넓게). 폭은 레일 고정.
        leftWidthCss={`${RAIL_WIDTH}px`}
        showLeftContent={false}
        rightWidthCss={`${effRightW}px`}
        onResizeRight={onResizeRight}
        // 동일 페이지의 두 사이드바 랜드마크 구별용 접근 이름(JCG-2100 마크업 복원).
        leftAriaLabel="섹션 내비게이션"
        rightAriaLabel="시험 정보 및 안내"
        // 슬림 헤더 — 좌측 콘텐츠 컬럼이 없어 사이드바 토글은 숨긴다.
        header={{ categoryLabel: "", showSidebarToggle: false }}
        // 우측(col 3): 카드 + RedAlert + 배너 슬롯
        right={
          <div
            ref={setRightSlot}
            className="flex min-w-0 flex-1 flex-col gap-4 overflow-y-auto p-3"
          />
        }
      >
        <div className="mx-auto w-full max-w-[1100px] px-4 pb-20 pt-2 sm:px-8">
          {children}
          {footer}
        </div>
      </ShellFrame>

      {globals}
    </TestCenterShellContext.Provider>
  );
}
